import {
  DashboardMarket,
  DashboardMarketAsset,
  Percent,
  PERCENT_ZERO,
} from "@silo/types";
import { ReactNode } from "react";
import { AssetContainer, DetailsContainer } from "./components";
import { PillContainer, PositionTypePill, TokenPill } from "./components/pills";
import {
  BalancePair,
  LiquidationPricePair,
  List,
  PointsPair,
} from "./components/list";
import { AprDisplayV2 } from "../apr-display-v2";
import { getNativeRewardsSymbol } from "./utils";
import { Array } from "effect";
import { DepositLiquidationPriceDescription } from "./components/deposit-liquidation-price-description";
import { DisabledCollateralWarning } from "../disabled-collateral-warning";
import { ActionContainer } from "./components/actions";

interface ProtectedAssetProps {
  className?: string;
  market: DashboardMarket;
  asset: DashboardMarketAsset;
  tokenLogo: ReactNode;
  divider?: boolean;
  isLoan: boolean;
  depositMoreLink: ReactNode;
  withdrawLink: ReactNode;
  revenueApr: Percent;
}

export function ProtectedAsset({
  className,
  market,
  asset,
  tokenLogo,
  divider,
  isLoan,
  depositMoreLink,
  withdrawLink,
  revenueApr,
}: ProtectedAssetProps): JSX.Element {
  const {
    protectedBalance,
    protectedBalanceUsd,
    decimals,
    protocolKey,
    protectedPoints,
    depositLiquidationPriceUsd,
    isDisabledCollateral,
    symbol,
  } = asset;

  const nativeRewardsSymbol = getNativeRewardsSymbol(protocolKey);

  return (
    <AssetContainer data-cy="deposit-asset-position">
      <DetailsContainer className={className}>
        <PillContainer>
          <TokenPill asset={asset} tokenLogo={tokenLogo} />

          <PositionTypePill className="text-amber-300">
            Non-borrowable deposit
          </PositionTypePill>
        </PillContainer>

        <List divider={divider}>
          <BalancePair
            label="Deposited"
            balance={protectedBalance}
            balanceUsd={protectedBalanceUsd}
            decimals={decimals}
          />

          <AprDisplayV2.PositionProtected
            asset={asset}
            nativeRewardsSymbol={nativeRewardsSymbol}
            revenueApr={revenueApr}
          />

          {Array.isNonEmptyReadonlyArray(protectedPoints) && (
            <PointsPair points={protectedPoints} />
          )}

          {isLoan && (
            <LiquidationPricePair
              liquidationPriceUsd={depositLiquidationPriceUsd}
              description={
                <DepositLiquidationPriceDescription
                  liquidationPriceUsd={depositLiquidationPriceUsd}
                />
              }
            />
          )}
        </List>
      </DetailsContainer>

      {divider && <div className="h-px bg-shale-700" />}

      {isDisabledCollateral && (
        <DisabledCollateralWarning assetSymbol={symbol} variant="panel" />
      )}

      <ActionContainer market={market} asset={asset}>
        {withdrawLink}

        {depositMoreLink}
      </ActionContainer>
    </AssetContainer>
  );
}
